/**
 * Balance de Préstamos — migración web de ares3040.
 *
 * Foto al día de los préstamos ACTIVOS: solicitado (factmot.financia, más
 * matrícula y ventas generales financiadas en las cuotas con la opción
 * "Incluir cargos en cuotas"),
 * interés teórico (financia * interés * plazo), pagado a capital e
 * interés y balances de capital/interés. Filtros opcionales por
 * sucursal y cartera (el filtro de cartera se oculta si el tenant no
 * maneja carteras) y rango por fecha de inicio del préstamo: "desde"
 * opcional, "hasta" obligatoria (hoy por defecto). Exporta a Excel e
 * imprime.
 *
 * Por préstamo también trae la frecuencia de pago, las cuotas vencidas a hoy
 * y el vencido (capital + interés de esas cuotas, sin mora). El filtro de
 * frecuencia / "solo con atraso" es local: totales, resumen por frecuencia,
 * Excel e impresiones salen sobre lo filtrado.
 */
import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import Autocomplete from "@mui/material/Autocomplete";
import { useApi, usePerfilSucursal, useSucursales } from "lib/api";
import { imprimirListadoGrande } from "lib/impresionhtml";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const fechaDMY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin datos — procese el reporte
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

// Delegado al formato compartido estilo factura x mayor (lib/impresionhtml).
function imprimirListado(titulo, subtitulo, cabeceras, filas, totales, opciones = {}) {
  imprimirListadoGrande({ titulo, subtitulo, cabeceras, filas, totales, ...opciones });
}

const ORDEN_FRECUENCIAS = ["Mensual", "Quincenal", "Semanal", "Diario"];
const posFrecuencia = (f) =>
  ORDEN_FRECUENCIAS.includes(f) ? ORDEN_FRECUENCIAS.indexOf(f) : ORDEN_FRECUENCIAS.length;
const ordenFrecuencia = (a, b) => posFrecuencia(a) - posFrecuencia(b) || a.localeCompare(b);

const num = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};
const r2 = (v) => Math.round(v * 100) / 100;

const CAMPOS_TOTAL = [
  "solicitado",
  "interesteorico",
  "vpagcap",
  "vpagint",
  "bcecapital",
  "bceinteres",
  "total",
  "vencido",
];

// Totales de un grupo de filas (lo filtrado o una frecuencia).
function sumar(filas) {
  const t = { cantidad: filas.length, cuotasvencidas: 0, convencido: 0 };
  CAMPOS_TOTAL.forEach((k) => {
    t[k] = 0;
  });
  filas.forEach((p) => {
    CAMPOS_TOTAL.forEach((k) => {
      t[k] += num(p[k]);
    });
    t.cuotasvencidas += num(p.cuotasvencidas);
    if (num(p.cuotasvencidas) > 0) t.convencido += 1;
  });
  CAMPOS_TOTAL.forEach((k) => {
    t[k] = r2(t[k]);
  });
  return t;
}

const moneyCol = (field, headerName, width = 110) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
});

export default function BalancePrestamos() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const { esadmin, sucursalPropia } = usePerfilSucursal();

  const [sucursal, setSucursal] = useState("");
  const sucursales = useSucursales();
  const [carteras, setCarteras] = useState([]);
  const [idcartera, setIdcartera] = useState("");
  // Financiamientos de motores (General) o préstamos personales
  // (prestamo.ppersonal): mismo flag que el informe de pagos.
  const [prestamosPersonales, setPrestamosPersonales] = useState(false);
  // Rango por fecha de inicio del préstamo. "Desde" vacío = sin piso
  // (el backend aplica su piso histórico); "hasta" es obligatoria.
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState(hoyISO());

  const [prestamos, setPrestamos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [procesado, setProcesado] = useState(false);
  // Sumar al solicitado la matrícula y las ventas generales financiadas en
  // las cuotas. incluirCargos es la opción pedida; incluyeCargos, lo que
  // devolvió el último proceso (para el subtítulo de la impresión y el Excel).
  const [incluirCargos, setIncluirCargos] = useState(false);
  const [incluyeCargos, setIncluyeCargos] = useState(false);
  // Filtros locales sobre lo ya procesado.
  const [frecuencia, setFrecuencia] = useState("");
  const [soloVencidos, setSoloVencidos] = useState(false);

  const frecuenciasDisponibles = useMemo(
    () => [...new Set(prestamos.map((p) => p.frecuencia || "Sin definir"))].sort(ordenFrecuencia),
    [prestamos]
  );
  // Si al reprocesar la frecuencia elegida ya no aparece, se suelta.
  useEffect(() => {
    if (frecuencia && !frecuenciasDisponibles.includes(frecuencia)) setFrecuencia("");
  }, [frecuenciasDisponibles, frecuencia]);

  const filas = useMemo(
    () =>
      prestamos.filter(
        (p) =>
          (!frecuencia || (p.frecuencia || "Sin definir") === frecuencia) &&
          (!soloVencidos || num(p.cuotasvencidas) > 0)
      ),
    [prestamos, frecuencia, soloVencidos]
  );
  const totales = useMemo(() => (procesado ? sumar(filas) : null), [filas, procesado]);
  // Resumen por frecuencia de pago (sobre lo filtrado).
  const porFrecuencia = useMemo(() => {
    const grupos = {};
    filas.forEach((p) => {
      const f = p.frecuencia || "Sin definir";
      (grupos[f] = grupos[f] || []).push(p);
    });
    return Object.keys(grupos)
      .sort(ordenFrecuencia)
      .map((f) => ({ frecuencia: f, filas: grupos[f], ...sumar(grupos[f]) }));
  }, [filas]);

  useEffect(() => {
    post("/api/prestamopersonal_config", {}).then(({ ok, data }) => {
      if (ok) setCarteras(data.carteras || []);
    });
    // Un usuario que no es admin queda fijado a su sucursal.
    if (!esadmin && sucursalPropia) setSucursal(sucursalPropia);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const procesar = async () => {
    if (!fechaHasta) {
      showAlert({ message: "Debe indicar la fecha hasta", color: "warning" });
      return;
    }
    if (fechaDesde && fechaDesde > fechaHasta) {
      showAlert({
        message: "La fecha desde no puede ser mayor que la fecha hasta",
        color: "warning",
      });
      return;
    }
    setLoading(true);
    try {
      const { ok, data } = await post("/api/balanceprestamos", {
        // El backend espera el código puro ("2"), no la etiqueta "2-Terrenas".
        sucursal: String(sucursal || "").split("-")[0].trim(),
        idcartera: idcartera || "",
        prestamospersonales: prestamosPersonales,
        fechadesde: fechaDesde || "",
        fechahasta: fechaHasta,
        incluircargoscuotas: incluirCargos,
      });
      if (ok) {
        setPrestamos(data.prestamos || []);
        setIncluyeCargos(Boolean(data.incluyecargoscuotas));
        setProcesado(true);
      } else {
        showAlert({ message: data.Error || "No se pudo generar el balance", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const carteraDescrip = (id) => {
    const c = carteras.find((k) => String(k.id) === String(id));
    return c ? c.descripcion : "";
  };

  const rangoTexto = fechaDesde
    ? `Iniciados del ${fechaDMY(fechaDesde)} al ${fechaDMY(fechaHasta)}`
    : `Iniciados hasta el ${fechaDMY(fechaHasta)}`;
  const subtitulo = `${rangoTexto}${sucursal ? ` · Sucursal ${sucursal}` : ""}${
    idcartera ? ` · Cartera ${carteraDescrip(idcartera)}` : ""
  }${frecuencia ? ` · Frecuencia ${frecuencia}` : ""}${
    soloVencidos ? " · Solo con cuotas vencidas" : ""
  }${incluyeCargos ? " · Solicitado incluye cargos financiados en cuotas" : ""}`;

  const exportar = () => {
    const hoja = XLSX.utils.aoa_to_sheet([
      ["Balance de Préstamos"],
      [subtitulo],
      [],
      [
        "Préstamo",
        "Fecha",
        "Cédula",
        "Nombres",
        "Sucursal",
        "Cartera",
        "Frecuencia",
        "Cuotas vencidas",
        "Días atraso",
        "Vencido",
        "Solicitado",
        "Int. Teórico",
        "Pag. Capital",
        "Pag. Interés",
        "Bce. Capital",
        "Bce. Interés",
        "Total",
      ],
      ...filas.map((p) => [
        p.noprest,
        p.fecha,
        p.cedula,
        p.nombres,
        p.sucursal,
        carteraDescrip(p.id_cartera),
        p.frecuencia,
        p.cuotasvencidas,
        p.diasatraso,
        p.vencido,
        p.solicitado,
        p.interesteorico,
        p.vpagcap,
        p.vpagint,
        p.bcecapital,
        p.bceinteres,
        p.total,
      ]),
      [],
      [
        "",
        "",
        "",
        "",
        "",
        "TOTAL",
        "",
        totales?.cuotasvencidas || 0,
        "",
        totales?.vencido || 0,
        totales?.solicitado || 0,
        totales?.interesteorico || 0,
        totales?.vpagcap || 0,
        totales?.vpagint || 0,
        totales?.bcecapital || 0,
        totales?.bceinteres || 0,
        totales?.total || 0,
      ],
    ]);
    const resumen = XLSX.utils.aoa_to_sheet([
      ["Vencido por frecuencia de pago"],
      [subtitulo],
      [],
      ["Frecuencia", "Préstamos", "Con atraso", "Cuotas vencidas", "Vencido", "Balance total"],
      ...porFrecuencia.map((g) => [
        g.frecuencia,
        g.cantidad,
        g.convencido,
        g.cuotasvencidas,
        g.vencido,
        g.total,
      ]),
      [
        "TOTAL",
        totales?.cantidad || 0,
        totales?.convencido || 0,
        totales?.cuotasvencidas || 0,
        totales?.vencido || 0,
        totales?.total || 0,
      ],
    ]);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Balance");
    XLSX.utils.book_append_sheet(libro, resumen, "Por frecuencia");
    XLSX.writeFile(libro, `BalancePrestamos_${fechaHasta || hoyISO()}.xlsx`);
  };

  const imprimir = () => {
    imprimirListado(
      "Balance de Préstamos",
      subtitulo,
      [
        "Préstamo",
        "Fecha",
        "Nombres",
        "Sucursal",
        "Frecuencia",
        "Ctas. venc.",
        "Vencido",
        "Solicitado",
        "Pag. Capital",
        "Pag. Interés",
        "Bce. Capital",
        "Bce. Interés",
        "Total",
      ],
      filas.map((p) => [
        p.noprest,
        p.fecha,
        p.nombres,
        p.sucursal,
        p.frecuencia,
        p.cuotasvencidas,
        fmtMoney(p.vencido),
        fmtMoney(p.solicitado),
        fmtMoney(p.vpagcap),
        fmtMoney(p.vpagint),
        fmtMoney(p.bcecapital),
        fmtMoney(p.bceinteres),
        fmtMoney(p.total),
      ]),
      [
        "",
        "",
        "",
        "TOTAL",
        "",
        totales?.cuotasvencidas ?? 0,
        fmtMoney(totales?.vencido),
        fmtMoney(totales?.solicitado),
        fmtMoney(totales?.vpagcap),
        fmtMoney(totales?.vpagint),
        fmtMoney(totales?.bcecapital),
        fmtMoney(totales?.bceinteres),
        fmtMoney(totales?.total),
      ],
      // 13 columnas: en vertical no cabían las de capital.
      { pagina: "letter landscape", numericas: [5, 6, 7, 8, 9, 10, 11, 12] }
    );
  };

  // Vencido por frecuencia: solo préstamos con cuotas vencidas, agrupados
  // por frecuencia (de más a menos vencido) con subtotal por grupo.
  // Ej.: "Juan Perez · Mensual · 5 cuotas vencidas · 20,000.00".
  const imprimirVencido = () => {
    const grupos = porFrecuencia
      .map((g) => ({ ...g, filas: g.filas.filter((p) => num(p.cuotasvencidas) > 0) }))
      .filter((g) => g.filas.length > 0);
    if (!grupos.length) {
      showAlert({ message: "No hay préstamos con cuotas vencidas", color: "info" });
      return;
    }
    const lineas = [];
    const subtotales = [];
    grupos.forEach((g) => {
      const vencidoGrupo = r2(g.filas.reduce((s, p) => s + num(p.vencido), 0));
      const totalGrupo = r2(g.filas.reduce((s, p) => s + num(p.total), 0));
      const cuotasGrupo = g.filas.reduce((s, p) => s + num(p.cuotasvencidas), 0);
      [...g.filas]
        .sort((a, b) => num(b.vencido) - num(a.vencido))
        .forEach((p) => {
          lineas.push([
            p.noprest,
            p.nombres,
            p.cedula,
            p.sucursal,
            g.frecuencia,
            p.cuotasvencidas,
            p.diasatraso,
            fmtMoney(p.vencido),
            fmtMoney(p.total),
          ]);
        });
      subtotales.push([
        "",
        `${g.frecuencia}: ${g.filas.length} préstamo${g.filas.length === 1 ? "" : "s"}`,
        "",
        "",
        g.frecuencia,
        cuotasGrupo,
        "",
        fmtMoney(vencidoGrupo),
        fmtMoney(totalGrupo),
      ]);
    });
    const conAtraso = grupos.flatMap((g) => g.filas);
    imprimirListadoGrande({
      titulo: "Vencido por Frecuencia de Pago",
      subtitulo,
      cabeceras: [
        "Préstamo",
        "Nombres",
        "Cédula",
        "Sucursal",
        "Frecuencia",
        "Ctas. venc.",
        "Días",
        "Vencido",
        "Balance",
      ],
      filas: lineas,
      totales: [
        "",
        "TOTAL",
        "",
        "",
        "",
        conAtraso.reduce((s, p) => s + num(p.cuotasvencidas), 0),
        "",
        fmtMoney(r2(conAtraso.reduce((s, p) => s + num(p.vencido), 0))),
        fmtMoney(r2(conAtraso.reduce((s, p) => s + num(p.total), 0))),
      ],
      totalesextra: subtotales,
      numericas: [5, 6, 7, 8],
    });
  };

  const columnas = [
    { field: "noprest", headerName: "Préstamo", width: 90 },
    { field: "fecha", headerName: "Fecha", width: 100 },
    { field: "cedula", headerName: "Cédula", width: 120 },
    { field: "nombres", headerName: "Nombres", flex: 1, minWidth: 170 },
    { field: "sucursal", headerName: "Sucursal", width: 100 },
    ...(carteras.length > 0
      ? [
          {
            field: "id_cartera",
            headerName: "Cartera",
            width: 120,
            valueGetter: (value) => carteraDescrip(value),
          },
        ]
      : []),
    { field: "frecuencia", headerName: "Frecuencia", width: 105 },
    {
      field: "cuotasvencidas",
      headerName: "Ctas. venc.",
      width: 95,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "diasatraso",
      headerName: "Días",
      width: 70,
      align: "center",
      headerAlign: "center",
    },
    {
      ...moneyCol("vencido", "Vencido", 120),
      cellClassName: (params) => (num(params.value) > 0 ? "celda-vencido" : ""),
    },
    moneyCol("solicitado", "Solicitado"),
    moneyCol("vpagcap", "Pag. Capital"),
    moneyCol("vpagint", "Pag. Interés"),
    moneyCol("bcecapital", "Bce. Capital"),
    moneyCol("bceinteres", "Bce. Interés"),
    moneyCol("total", "Total", 120),
  ];

  const hayDatos = filas.length > 0;

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Balance de Préstamos"
        subtitle="Balances de capital e interés de los préstamos activos"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde (opcional)"
                InputLabelProps={{ shrink: true }}
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                required
                InputLabelProps={{ shrink: true }}
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2.5}>
              <Autocomplete
                options={sucursales.map((s) => s.label)}
                value={sucursal || null}
                onChange={(_, v) => setSucursal(v || "")}
                disabled={!esadmin}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="Sucursal (opcional)" sx={fpInputSx} />
                )}
              />
            </Grid>
            {/* General = financiamientos de motores; Personal = préstamos
                personales (mismo corte que el informe de pagos). */}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Tipo de préstamo"
                value={prestamosPersonales ? "personal" : "general"}
                onChange={(e) => setPrestamosPersonales(e.target.value === "personal")}
                sx={fpInputSx}
              >
                <MenuItem value="general">General</MenuItem>
                <MenuItem value="personal">Personal</MenuItem>
              </TextField>
            </Grid>
            {carteras.length > 0 && (
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Cartera"
                  value={idcartera}
                  onChange={(e) => setIdcartera(e.target.value)}
                  sx={fpInputSx}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {carteras.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.descripcion}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Frecuencia"
                value={frecuencia}
                onChange={(e) => setFrecuencia(e.target.value)}
                disabled={!procesado}
                sx={fpInputSx}
              >
                <MenuItem value="">Todas</MenuItem>
                {frecuenciasDisponibles.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md="auto">
              <FormControlLabel
                control={
                  <Switch
                    checked={incluirCargos}
                    onChange={(e) => setIncluirCargos(e.target.checked)}
                  />
                }
                label="Incluir cargos en cuotas"
                title="Suma al solicitado la matrícula y las ventas generales financiadas dentro de las cuotas"
                sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.85rem", fontWeight: 600 } }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md="auto">
              <FormControlLabel
                control={
                  <Switch
                    checked={soloVencidos}
                    onChange={(e) => setSoloVencidos(e.target.checked)}
                    disabled={!procesado}
                  />
                }
                label="Solo con atraso"
                sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.85rem", fontWeight: 600 } }}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip label="CANTIDAD" value={totales?.cantidad ?? 0} />
            <ResumenChip label="SOLICITADO" value={`RD$ ${fmtMoney(totales?.solicitado)}`} />
            <ResumenChip
              label="BCE CAPITAL"
              value={`RD$ ${fmtMoney(totales?.bcecapital)}`}
              color="#2E7D32"
            />
            <ResumenChip
              label="BCE INTERÉS"
              value={`RD$ ${fmtMoney(totales?.bceinteres)}`}
              color="#EF6C00"
            />
            <ResumenChip label="TOTAL" value={`RD$ ${fmtMoney(totales?.total)}`} color="#C62828" />
            <ResumenChip
              label="TOTAL VENCIDO"
              value={`RD$ ${fmtMoney(totales?.vencido)}`}
              color={FP.color.danger}
            />
            <ResumenChip
              label="CUOTAS VENCIDAS"
              value={`${totales?.cuotasvencidas ?? 0} (${totales?.convencido ?? 0} préstamos)`}
              color={FP.color.danger}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimirVencido}
              disabled={!hayDatos || !totales?.convencido}
              sx={btnOutline(FP.color.danger)}
            >
              Imprimir vencido
            </Button>
          </Stack>

          {/* Vencido por frecuencia de pago */}
          {porFrecuencia.length > 0 && (
            <Box
              sx={{
                mb: 1.6,
                border: `1px solid ${FP.color.border}`,
                borderRadius: 1.8,
                bgcolor: "#fff",
                overflowX: "auto",
              }}
            >
              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.82rem",
                  "& th, & td": { px: 1.5, py: 0.8, whiteSpace: "nowrap" },
                  "& th": {
                    textAlign: "left",
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    color: FP.color.textMuted,
                    borderBottom: `1px solid ${FP.color.border}`,
                  },
                  "& td.n, & th.n": { textAlign: "right" },
                  "& tbody tr": { cursor: "pointer" },
                  "& tbody tr:hover": { bgcolor: FP.color.surfaceAlt },
                  "& tfoot td": { fontWeight: 800, borderTop: `1px solid ${FP.color.border}` },
                }}
              >
                <thead>
                  <tr>
                    <th>FRECUENCIA</th>
                    <th className="n">PRÉSTAMOS</th>
                    <th className="n">CON ATRASO</th>
                    <th className="n">CUOTAS VENCIDAS</th>
                    <th className="n">VENCIDO</th>
                    <th className="n">BALANCE TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {porFrecuencia.map((g) => (
                    <Box
                      component="tr"
                      key={g.frecuencia}
                      title="Filtrar por esta frecuencia"
                      onClick={() =>
                        setFrecuencia((actual) => (actual === g.frecuencia ? "" : g.frecuencia))
                      }
                      sx={frecuencia === g.frecuencia ? { bgcolor: FP.color.accentBlueSoft } : {}}
                    >
                      <td style={{ fontWeight: 700 }}>{g.frecuencia}</td>
                      <td className="n">{g.cantidad}</td>
                      <td className="n">{g.convencido}</td>
                      <td className="n">{g.cuotasvencidas}</td>
                      <td className="n" style={{ color: FP.color.danger, fontWeight: 700 }}>
                        {fmtMoney(g.vencido)}
                      </td>
                      <td className="n">{fmtMoney(g.total)}</td>
                    </Box>
                  ))}
                </tbody>
                {porFrecuencia.length > 1 && (
                  <tfoot>
                    <tr>
                      <td>Total</td>
                      <td className="n">{totales?.cantidad ?? 0}</td>
                      <td className="n">{totales?.convencido ?? 0}</td>
                      <td className="n">{totales?.cuotasvencidas ?? 0}</td>
                      <td className="n" style={{ color: FP.color.danger }}>
                        {fmtMoney(totales?.vencido)}
                      </td>
                      <td className="n">{fmtMoney(totales?.total)}</td>
                    </tr>
                  </tfoot>
                )}
              </Box>
            </Box>
          )}

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={filas}
              columns={columnas}
              getRowId={(r) => r.noprest}
              loading={loading}
              rowHeight={44}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{
                ...fpDataGridSx,
                "--DataGrid-overlayHeight": "220px",
                "& .celda-vencido": { color: FP.color.danger, fontWeight: 700 },
              }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
